import { translate, formatPriceApp } from "utils";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import { APP_PATH } from "constants/path";
import { Tag, Button } from "antd";
import moment from "moment";
import {
	SubscriptionTransactionQueryParams,
	SubscriptionTransaction,
} from "features/subscription-transaction/type";
import { selectLang } from "store/lang/langSlice";
import { useSelector } from "react-redux";
import {
	subscriptionTransactionStatus,
	paymentMethods,
} from "features/subscription-transaction/constants";
import { useParams } from "react-router-dom";
import useResponsive from "hooks/useResponsive";
import { EyeOutlined } from "@ant-design/icons";

const useColumn = ({
	query,
}: {
	query: SubscriptionTransactionQueryParams;
}): ColumnsType<SubscriptionTransaction> => {
	const { isMobile } = useResponsive();
	const lang = useSelector(selectLang);
	const { id: merchantId = "" } = useParams();
	const columns: ColumnsType<SubscriptionTransaction> = [
		{
			title: translate("NO"),
			dataIndex: "",
			key: "NO",
			width: "50px",
			align: "center",
			render: (_, __, index: number) => (
				<p>{query.skip * query.take + index + 1}</p>
			),
		},
		{
			title: translate("ID"),
			dataIndex: "id",
			key: "id",
			render: id => {
				return (
					<Link
						to={
							merchantId
								? `${APP_PATH.merchants}/${merchantId}/billing-infor/${id}`
								: `${APP_PATH.transactionBillingInfor}/${id}`
						}
						className="text-blue-500"
					>
						{id}
					</Link>
				);
			},
		},
		{
			title: translate("Merchant"),
			dataIndex: "merchant",
			key: "merchant",
			width: "200px",
			render: merchant => merchant?.fullName,
		},
		{
			title: translate("Package name"),
			dataIndex: "extraData",
			key: "extraData",
			render: extraData => {
				return extraData.package.name[lang];
			},
		},
		{
			title: translate("Total Payment"),
			dataIndex: "total",
			key: "total",
			render: total => {
				return `${formatPriceApp(total)} USD`;
			},
		},
		{
			title: translate("Payment type"),
			dataIndex: "method",
			key: "method",
			render: method => {
				const methodFound = paymentMethods.find(el => el.value === method);
				return translate(methodFound?.name || "");
			},
		},
		{
			title: translate("Created date"),
			dataIndex: "merchantSubscription",
			key: "merchantSubscription",
			render: merchantSubscription => {
				return (
					<p>{moment(merchantSubscription.startDate).format("DD/MM/YYYY")}</p>
				);
			},
		},
		{
			title: translate("Valid to"),
			dataIndex: "merchantSubscription",
			key: "merchantSubscription",
			render: (merchantSubscription, record) => {
				return (
					<p>
						{moment(merchantSubscription.startDate)
							.add(record.quotation, "M")
							.format("DD/MM/YYYY")}
					</p>
				);
			},
		},
		{
			title: translate("Status"),
			dataIndex: "status",
			key: "status",
			render: (status, record) => {
				const statusFound = subscriptionTransactionStatus.find(
					el => el.value === status,
				);
				if (statusFound) {
					return (
						<div className="text-center">
							<Tag color={statusFound.color}>{translate(statusFound.name)}</Tag>
						</div>
					);
				}
				return null;
			},
		},
		{
			title: translate(`Action`),
			key: "operation",
			fixed: isMobile ? false : "right",
			width: isMobile ? "70px" : "100px",
			render: (text, record) => (
				<div className="flex justify-center">
					<Link
						to={
							merchantId
								? `${APP_PATH.merchants}/${merchantId}/billing-infor/${record.id}`
								: `${APP_PATH.transactionBillingInfor}/${record.id}`
						}
					>
						<Button
							type="primary"
							className="m-1 flex items-center justify-center bg-[#1890ff]"
							icon={<EyeOutlined />}
							shape="circle"
							size={isMobile ? "small" : "middle"}
						/>
					</Link>
				</div>
			),
		},
	];
	return merchantId ? columns.filter(col => col.key !== "merchant") : columns;
};

export default useColumn;
